import React from "react";
import { useSidePanel } from "@rebasepro/cms";
import { ExternalLinkIcon as RebaseExternalLinkIcon, iconSize } from "@rebasepro/ui";

const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;

/**
 * A related record, named, and openable.
 *
 * The panel named things everywhere and opened them almost nowhere: a
 * candidature said "Auditoría Interna en Sistemas de Gestión Basura Cero"
 * in plain text, a conversation said "Candidato — cambiar" without the
 * candidate, a company's cover listed its vacancies as labels. Every one
 * of those is a row in a collection the model already joins to, and the
 * CMS can open any row as a side dialog over whatever screen you are on.
 *
 * So: one link. It looks like a link, opens the record's cover in a side
 * dialog (the `resumen` tab, where every record opens by default), and
 * closing it leaves the screen where it was.
 *
 * Conversations open the same way and are drawn the same way, but through
 * `ConversationLink` (ConversationPanel.tsx): a conversation has no cover to
 * open — its record is a mailbox, not a form — so it gets a panel of its own.
 * It used to be a kind here that navigated to Correo instead, which threw
 * away the screen you were on and then, because of a race in the mailbox's
 * first selection, landed on the top of the queue rather than on the
 * conversation you clicked.
 */
export type LinkKind = "talent" | "job" | "company";

/** What a link to another record looks like, whichever panel it opens. */
export const entityLinkCls = "inline-flex items-center gap-1 text-primary hover:underline text-left";

/**
 * The id of a related record, whatever shape the relation arrived in.
 *
 * A relation column resolves *in place*. Ask for `include: ["*"]` and
 * `company_id` stops being a uuid: it becomes the target itself, in one of
 * three shapes — a reference around an Entity around the row (the admin),
 * the flat row (the HTTP API), or a bare uuid (wherever nothing included
 * it). `String()` on either of the first two yields "[object Object]",
 * which is how a vacancy's company link came to point at
 * `/c/companies/[object%20Object]/resumen`.
 *
 * Every shape carries the id at the top, so read it there and fall back
 * inwards.
 */
export function entityIdOf(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === "string") return value.trim() || null;
    if (typeof value !== "object") return String(value);
    const ref = value as Record<string, any>;
    const id = ref.id ?? ref.data?.id ?? ref.data?.values?.id ?? ref.values?.id;
    return id ? String(id) : null;
}

const PATH: Record<LinkKind, string> = {
    talent: "talents",
    job: "jobs",
    company: "companies"
};

export function EntityLink({
    kind,
    id,
    children,
    tab,
    className
}: {
    kind: LinkKind;
    /** A uuid, or the relation column itself — see `entityIdOf`. */
    id: unknown;
    children: React.ReactNode;
    /** The tab to open on. Defaults to the cover. */
    tab?: string;
    className?: string;
}) {
    const sidePanel = useSidePanel();
    const entityId = entityIdOf(id);
    if (!entityId) return <>{children}</>;

    const cls = [entityLinkCls, className].filter(Boolean).join(" ");

    return (
        <button
            type="button"
            className={cls}
            onClick={(e) => {
                // Inside a clickable card, opening the record must not also
                // select the card.
                e.stopPropagation();
                sidePanel.open({ path: PATH[kind], entityId, selectedTab: tab ?? "resumen" });
            }}
        >
            {children}
            <ExternalLinkIcon size={iconSize.smallest} className="opacity-60 shrink-0" />
        </button>
    );
}
